import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Truck, Award, Users, ChevronRight, ArrowRight, Factory, Package, CheckCircle, Layers, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import WhatsAppButton from '@/components/WhatsAppButton';
import HeroCarousel from '@/components/HeroCarousel';
import ProductBannerSlider from '@/components/ProductBannerSlider';
import StatsCounter from '@/components/StatsCounter';
import IndiaMap3D from '@/components/IndiaMap3D';
import CorporateGifting from '@/components/CorporateGifting';
import DailyInspiration from '@/components/DailyInspiration';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'RSD Accessories – B2B Wholesale Mobile Accessories Manufacturer India';
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data;
      
      const featured = [
        allProducts.find(p => p.category === 'data-cables'),
        ...allProducts.filter(p => p.category === 'mobile-chargers').slice(0, 3),
        allProducts.find(p => p.category === 'car-chargers'),
        allProducts.find(p => p.category === 'earbuds')
      ].filter(Boolean);
      
      setFeaturedProducts(featured);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'data-cables', name: 'Data Cables', image: '/images/cat-data-cables.png' },
    { id: 'mobile-chargers', name: 'Mobile Chargers', image: '/images/cat-mobile-chargers.jpg' },
    { id: 'car-chargers', name: 'Car Chargers', image: '/images/cat-car-chargers.jpg' },
    { id: 'earbuds', name: 'Earbuds', image: '/images/cat-earbuds.png' },
    { id: 'neckbands', name: 'Neckbands', image: '/images/cat-neckbands.png' },
    { id: 'wired-handfree', name: 'Wired Handfree', image: '/images/cat-wired-handfree.png' }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'Premium materials with rigorous quality control'
    },
    {
      icon: Truck,
      title: 'Pan-India Reach',
      description: 'Fast delivery across all major cities'
    },
    {
      icon: Award,
      title: 'Warranty',
      description: '1 Year warranty on all products'
    },
    {
      icon: Users,
      title: 'Made in India',
      description: 'Supporting local manufacturing excellence'
    }
  ];

  return (
    <div className="-mt-1">
      {/* Premium Animated Marquee Banner - fills the white gap */}
      <div className="bg-gradient-to-r from-[#FF4D00] via-orange-500 to-[#FF4D00] py-2 overflow-hidden">
        <div className="relative flex overflow-x-hidden">
          <div className="animate-marquee whitespace-nowrap flex items-center">
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              ⚡ 65W Super Fast Charging
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🏭 Made in India
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🚚 Pan India Delivery
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              📞 +91-9311211889
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🏆 1 Year Warranty
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
          </div>
          <div className="animate-marquee2 absolute top-0 whitespace-nowrap flex items-center">
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              ⚡ 65W Super Fast Charging
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🏭 Made in India
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🚚 Pan India Delivery
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              📞 +91-9311211889
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
            <span className="mx-6 text-white text-xs sm:text-sm font-semibold flex items-center gap-2">
              🏆 1 Year Warranty
            </span>
            <span className="mx-4 text-white/60 text-xs">•</span>
          </div>
        </div>
      </div>
      
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Daily Inspiration - Thoughtful Quotes Section */}
      <DailyInspiration />

      {/* NEW LAUNCH - Premium 65W Charger Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black overflow-hidden" data-testid="new-launch-section">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23B8860B' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-amber-500/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-[#FF4D00]/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* NEW LAUNCH Badge */}
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black px-5 py-2 rounded-full text-sm font-black mb-6 shadow-lg shadow-amber-500/30 animate-pulse">
                <span className="w-2 h-2 bg-black rounded-full animate-ping"></span>
                NEW LAUNCH
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight">
                <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 bg-clip-text text-transparent">RSD 65W</span>
                <br />
                <span className="text-white">FAST CHARGER</span>
              </h2>
              
              <p className="text-2xl md:text-3xl font-bold text-amber-400 mb-4">
                Power. Speed. Reliability.
              </p>
              
              <p className="text-lg text-neutral-400 mb-6 max-w-lg mx-auto lg:mx-0">
                The Next Generation of Fast Charging is Here. <span className="text-amber-500 font-semibold">Matte Metallic Gray Edition</span> - Bold. Powerful. Unmistakably RSD.
              </p>
              
              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">65W Super</p>
                    <p className="text-neutral-400 text-xs">Fast Charging</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Advanced</p>
                    <p className="text-neutral-400 text-xs">Safety Protection</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Low Heat</p>
                    <p className="text-neutral-400 text-xs">Technology</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Universal</p>
                    <p className="text-neutral-400 text-xs">Compatibility</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/product/rsd-65w-matte-metallic-gray-charger">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-black px-8 py-6 rounded-xl text-lg font-black shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transition-all duration-300 hover:-translate-y-1">
                    View Product
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="https://wa.me/919311211889?text=Hi, I'm interested in the new RSD 65W Matte Metallic Gray Charger. Please share bulk pricing." target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-amber-500/50 text-amber-500 hover:bg-amber-500/10 px-8 py-6 rounded-xl text-lg font-bold">
                    Request Bulk Quote
                  </Button>
                </a>
              </div>
              
              {/* Tagline */}
              <p className="mt-6 text-neutral-500 text-sm italic">
                Engineered for Performance
              </p>
            </div>
            
            {/* Right - Product Image */}
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-yellow-500/20 rounded-3xl blur-3xl"></div>
              
              {/* Image Container */}
              <div className="relative bg-gradient-to-br from-neutral-800/50 to-neutral-900/50 backdrop-blur-sm border border-amber-500/20 rounded-3xl p-8 hover:border-amber-500/40 transition-all duration-500">
                <img 
                  src="/images/65w-charger.jpg"
                  alt="RSD 65W Fast Charger - Matte Metallic Gray Edition"
                  className="w-full rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500"
                />
                
                {/* Edition Badge */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-600 to-yellow-600 text-black px-4 py-2 rounded-full text-xs font-black shadow-lg">
                  MATTE METALLIC GRAY EDITION
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Trust Badge Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-neutral-900 via-neutral-800 to-black relative overflow-hidden" data-testid="trust-badge-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px'}}></div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#FF4D00]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF4D00]/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Main Badge Heading */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-5 py-2 rounded-full text-sm font-bold mb-6 shadow-lg shadow-[#FF4D00]/30">
              <Award className="w-4 h-4" />
              INDUSTRY RECOGNITION
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
              Ranked Among <span className="text-[#FF4D00]">Top Data Cable</span>
              <br className="hidden md:block" />
              <span className="text-[#FF4D00]">Manufacturers</span> in Noida
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              Trusted by 500+ brands across India for premium quality mobile accessories manufacturing
            </p>
          </div>

          {/* Trust Badges Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {/* Google Reviews Badge */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-[#FF4D00]/30 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <p className="text-3xl md:text-4xl font-black text-white mb-1">215+</p>
              <p className="text-neutral-400 text-sm font-medium">Google Reviews</p>
            </div>

            {/* Star Rating Badge */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
              <p className="text-3xl md:text-4xl font-black text-white mb-1">4.9</p>
              <p className="text-neutral-400 text-sm font-medium">Star Rating</p>
              <div className="flex justify-center gap-0.5 mt-2">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                ))}
              </div>
            </div>

            {/* Own Brand Badge */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-[#FF4D00] to-red-600 rounded-xl flex items-center justify-center shadow-lg shadow-[#FF4D00]/30 group-hover:scale-110 transition-transform">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <p className="text-xl md:text-2xl font-black text-white mb-1">RSD</p>
              <p className="text-[#FF4D00] text-xs font-bold mb-1">A Brand of New Eon</p>
              <p className="text-neutral-400 text-sm font-medium">Own Brand</p>
            </div>

            {/* OEM Manufacturing Badge */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <Factory className="w-7 h-7 text-white" />
              </div>
              <p className="text-xl md:text-2xl font-black text-white mb-1">OEM</p>
              <p className="text-neutral-400 text-sm font-medium">Manufacturing</p>
              <p className="text-blue-400 text-xs mt-1 font-medium">Private Label Ready</p>
            </div>

            {/* Pan India Supply Badge */}
            <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:border-[#FF4D00]/50 transition-all duration-300 col-span-2 md:col-span-1">
              <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <p className="text-xl md:text-2xl font-black text-white mb-1">Pan India</p>
              <p className="text-neutral-400 text-sm font-medium">Supply Network</p>
              <p className="text-green-400 text-xs mt-1 font-medium">All 28 States</p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-10 text-center">
            <Link to="/data-cable-manufacturer-in-noida">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full font-bold shadow-lg shadow-[#FF4D00]/30 hover:shadow-xl hover:shadow-[#FF4D00]/40 transition-all" data-testid="trust-section-cta">
                Explore Our Manufacturing
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Premium Product Banner Slider */}
      <ProductBannerSlider />

      {/* Categories Section with Wave Divider */}
      <section className="py-16 md:py-24 bg-white relative">
        {/* Wave Divider Top */}
        <div className="absolute top-0 left-0 right-0 overflow-hidden leading-none">
          <svg className="relative block w-full h-12" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#f5f5f5"></path>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              PRODUCT CATEGORIES
            </span>
            <h2 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-neutral-900 via-neutral-700 to-neutral-900 bg-clip-text text-transparent">
              Shop by Category
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Explore our wide range of premium mobile accessories for your business
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/shop/${category.id}`}
                data-testid={`category-${category.id}`}
                className="group relative aspect-square rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70 group-hover:opacity-90 transition-opacity"></div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#FF4D00] rounded-2xl transition-all duration-300"></div>
                
                {/* Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-end p-4">
                  <h3 className="text-white font-bold text-sm text-center group-hover:text-[#FF4D00] transition-colors duration-300">
                    {category.name}
                  </h3>
                  <span className="text-white/70 text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    View Collection →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-neutral-50 to-white relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#FF4D00]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-100/50 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-14 gap-4">
            <div>
              <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
                TOP PICKS
              </span>
              <h2 className="text-4xl md:text-5xl font-black mb-3 bg-gradient-to-r from-neutral-900 to-neutral-600 bg-clip-text text-transparent">
                Featured Products
              </h2>
              <p className="text-lg text-neutral-600">Our most popular B2B accessories</p>
            </div>
            <Link to="/shop">
              <Button
                data-testid="view-all-products"
                className="hidden md:flex bg-gradient-to-r from-[#FF4D00] to-orange-500 hover:from-[#e64500] hover:to-orange-600 text-white rounded-full px-8 py-6 font-bold shadow-lg shadow-[#FF4D00]/20 hover:shadow-xl hover:shadow-[#FF4D00]/30 transition-all duration-300 hover:-translate-y-1"
              >
                View All Products
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FF4D00]/20 border-t-[#FF4D00]"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#FF4D00]">RSD</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product, index) => (
                <div key={product.id} style={{ animationDelay: `${index * 100}ms` }} className="animate-fadeIn">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}

          {/* Mobile View All Button */}
          <div className="mt-10 text-center md:hidden">
            <Link to="/shop">
              <Button className="bg-gradient-to-r from-[#FF4D00] to-orange-500 text-white rounded-full px-8 py-4 font-bold shadow-lg">
                View All Products
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* OEM & Private Label Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-neutral-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/10 text-[#FF4D00] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Factory className="w-4 h-4" />
                Manufacturing Partner
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                OEM & Private Label<br />
                <span className="text-[#FF4D00]">Manufacturing</span>
              </h2>
              <p className="text-lg text-neutral-600 mb-8 leading-relaxed">
                Along with our own brand RSD Accessories, we provide OEM and Private Label 
                manufacturing services for businesses looking to build or expand their own brand.
              </p>
              
              {/* OEM Benefits */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 bg-[#FF4D00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">High-Quality Manufacturing</h4>
                    <p className="text-neutral-500 text-xs">Premium materials & processes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 bg-[#FF4D00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Custom Branding & Packaging</h4>
                    <p className="text-neutral-500 text-xs">Your brand, our expertise</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 bg-[#FF4D00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Quality Control & Testing</h4>
                    <p className="text-neutral-500 text-xs">Rigorous QC standards</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-10 h-10 bg-[#FF4D00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Layers className="w-5 h-5 text-[#FF4D00]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm">Scalable Production</h4>
                    <p className="text-neutral-500 text-xs">From startup to enterprise</p>
                  </div>
                </div>
              </div>

              <p className="text-neutral-600 mb-8 italic">
                Whether you are a startup, wholesaler, or established brand — 
                we help you bring reliable mobile accessories to market.
              </p>

              {/* Single CTA Button - Email for OEM */}
              <a 
                href="mailto:contact@rsdaccessories.com?subject=OEM%20Inquiry%20-%20Private%20Label%20Manufacturing&body=Hello%20RSD%20Team%2C%0A%0AI%20am%20interested%20in%20your%20OEM%20%2F%20Private%20Label%20manufacturing%20services.%0A%0ACompany%20Name%3A%0AProduct%20Interest%3A%0AMonthly%20Quantity%3A%0AContact%20Number%3A%0A%0APlease%20share%20more%20details.%0A%0AThank%20you."
                data-testid="oem-contact-btn"
              >
                <Button
                  className="bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full px-8 py-6 text-base font-bold"
                >
                  Contact for OEM
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>

            {/* Right Image/Visual */}
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/site/site_43.png"
                  alt="RSD Manufacturing Facility"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FF4D00]/10 rounded-full -z-10"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-black/5 rounded-full -z-10"></div>
              
              {/* Trust Badge */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-full shadow-lg border border-neutral-100">
                <p className="text-sm font-semibold text-neutral-800 text-center">
                  🇮🇳 From Manufacturing to Market, <span className="text-[#FF4D00]">Built on Trust</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Gifting Section */}
      <CorporateGifting />

      {/* Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose RSD Accessories</h2>
            <p className="text-lg text-neutral-600">Quality, Trust, and Innovation</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  data-testid={`feature-${index}`}
                  className="bg-neutral-50 p-8 rounded-2xl hover:border hover:border-neutral-200 transition-all text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF4D00] rounded-full mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-neutral-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Counter Section */}
      <StatsCounter />

      {/* 3D Distributor Map Section */}
      <IndiaMap3D />

      {/* Explore Our Manufacturing Services in India - SEO Internal Linking Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-neutral-50 to-white" data-testid="manufacturing-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FF4D00]/10 text-[#FF4D00] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Factory className="w-4 h-4" />
              B2B Manufacturing Services
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Explore Our <span className="text-[#FF4D00]">Manufacturing Services</span> in India
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              RSD Accessories Pvt Ltd is a leading mobile accessories manufacturer in Noida, India. 
              We offer OEM, ODM, and private label manufacturing solutions for data cables, chargers, earbuds, and more.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Data Cable Manufacturer Link */}
            <Link 
              to="/data-cable-manufacturer-in-noida" 
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-100"
              data-testid="link-data-cable-manufacturer"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/images/site/site_38.jpg"
                  alt="Best Data Cable Manufacturer in Noida - Premium Quality OEM Factory"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#FF4D00] text-sm font-semibold mb-2">
                  <Factory className="w-4 h-4" />
                  OEM/ODM Manufacturing
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2 group-hover:text-[#FF4D00] transition-colors">
                  Data Cable Manufacturer in Noida
                </h3>
                <p className="text-neutral-600 text-sm mb-4">
                  Premium quality data cables - Type-C, Micro USB, Lightning. 3L+ cables/month capacity with custom branding.
                </p>
                <span className="inline-flex items-center text-[#FF4D00] font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Explore Data Cable Manufacturing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>

            {/* Mobile Charger Manufacturer Link */}
            <Link 
              to="/best-mobile-charger-manufacturer-in-noida" 
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-100"
              data-testid="link-charger-manufacturer"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/images/site/site_39.jpg"
                  alt="Best Mobile Charger Manufacturer in Noida - Fast Charging OEM Factory"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#FF4D00] text-sm font-semibold mb-2">
                  <Factory className="w-4 h-4" />
                  OEM/ODM Manufacturing
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2 group-hover:text-[#FF4D00] transition-colors">
                  Mobile Charger Manufacturer in Noida
                </h3>
                <p className="text-neutral-600 text-sm mb-4">
                  15W to 120W fast chargers - PD, QC, VOOC, WARP compatible. Premium quality with custom packaging.
                </p>
                <span className="inline-flex items-center text-[#FF4D00] font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Explore Charger Manufacturing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>

            {/* OEM Mobile Accessories Link */}
            <Link 
              to="/oem-mobile-accessories-manufacturer-in-india" 
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-100"
              data-testid="link-oem-manufacturer"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="/images/site/site_37.png"
                  alt="Best OEM Mobile Accessories Manufacturer in India - Private Label Factory"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#FF4D00] text-sm font-semibold mb-2">
                  <Factory className="w-4 h-4" />
                  Private Label Factory
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2 group-hover:text-[#FF4D00] transition-colors">
                  OEM Mobile Accessories Manufacturer
                </h3>
                <p className="text-neutral-600 text-sm mb-4">
                  Complete OEM/ODM solutions - cables, chargers, earbuds, neckbands. Your brand, our manufacturing expertise.
                </p>
                <span className="inline-flex items-center text-[#FF4D00] font-semibold text-sm group-hover:translate-x-2 transition-transform">
                  Explore OEM Manufacturing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>
          </div>

          {/* Additional SEO Links */}
          <div className="mt-12 text-center">
            <p className="text-neutral-600 mb-6">
              Looking for reliable B2B mobile accessories manufacturing partner in India? RSD Accessories is your trusted OEM/ODM factory.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/become-distributor" 
                className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-[#FF4D00] hover:text-white text-neutral-700 px-6 py-3 rounded-full font-medium transition-all"
              >
                Become a Distributor
              </Link>
              <Link 
                to="/about" 
                className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-[#FF4D00] hover:text-white text-neutral-700 px-6 py-3 rounded-full font-medium transition-all"
              >
                About Our Factory
              </Link>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-[#FF4D00] hover:text-white text-neutral-700 px-6 py-3 rounded-full font-medium transition-all"
              >
                Request Bulk Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Locations Section - SEO Internal Linking */}
      <section className="py-16 md:py-20 bg-neutral-100" data-testid="manufacturing-locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#FF4D00]/10 text-[#FF4D00] font-bold rounded-full text-sm mb-4">
              MADE IN INDIA
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-4">
              Our Manufacturing Locations
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              RSD Accessories operates state-of-the-art manufacturing facilities in Noida, Delhi NCR. Visit our specialized pages to learn more about our production capabilities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/best-data-cable-factory-in-noida"
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-200 hover:border-[#FF4D00]"
            >
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                <Factory className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg mb-2 group-hover:text-[#FF4D00] transition-colors">Data Cable Factory Noida</h3>
              <p className="text-sm text-neutral-600 mb-3">State-of-the-art manufacturing unit in Noida with 50K+ daily production capacity.</p>
              <span className="text-[#FF4D00] font-semibold text-sm inline-flex items-center">
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
            
            <Link 
              to="/best-data-cable-factory-in-delhi"
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-200 hover:border-[#FF4D00]"
            >
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                <Factory className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg mb-2 group-hover:text-[#FF4D00] transition-colors">Data Cable Factory Delhi</h3>
              <p className="text-sm text-neutral-600 mb-3">Premium data cable manufacturing in Delhi NCR serving 500+ dealers across India.</p>
              <span className="text-[#FF4D00] font-semibold text-sm inline-flex items-center">
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
            
            <Link 
              to="/best-mobile-charger-manufacturer-in-noida"
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-200 hover:border-[#FF4D00]"
            >
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                <Package className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg mb-2 group-hover:text-[#FF4D00] transition-colors">Mobile Charger Manufacturer</h3>
              <p className="text-sm text-neutral-600 mb-3">Fast chargers from 15W to 65W with BIS certification. Wholesale supply across India.</p>
              <span className="text-[#FF4D00] font-semibold text-sm inline-flex items-center">
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
            
            <Link 
              to="/oem-mobile-accessories-manufacturer-in-india"
              className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-200 hover:border-[#FF4D00]"
            >
              <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                <Award className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-lg mb-2 group-hover:text-[#FF4D00] transition-colors">OEM Accessories Manufacturer</h3>
              <p className="text-sm text-neutral-600 mb-3">Private label manufacturing with custom branding, packaging, and design support.</p>
              <span className="text-[#FF4D00] font-semibold text-sm inline-flex items-center">
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* B2B CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-neutral-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
            <Factory className="w-4 h-4" />
            B2B WHOLESALE ONLY
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Partner with India&apos;s Leading Mobile Accessories Manufacturer
          </h2>
          <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
            OEM/ODM solutions, private label manufacturing, and bulk wholesale supply. 
            Minimum Order Quantity applies. Contact us for best B2B pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919311211889">
              <Button
                data-testid="cta-call"
                className="bg-[#FF4D00] hover:bg-[#E04400] text-white rounded-full px-8 py-6 text-lg font-bold"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call: +91-9311211889
              </Button>
            </a>
            <Link to="/become-distributor">
              <Button
                data-testid="cta-distributor"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-neutral-900 rounded-full px-8 py-6 text-lg font-bold"
              >
                Become a Distributor
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default Home;